import './SliderStack.css'

import clsx from 'clsx'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

const BASE_Z = 190
const CLOSE_MS = 300 // matches --slider-stack-slide-duration in SliderStack.css
const DEFAULT_WIDTH = '48rem'
const SCRIM = [0, 0.45, 0.6]

type StackItem = { id: string; onClose?: () => void; isClosing?: boolean }

const Ctx = createContext<{
  items: StackItem[]
  pop: (id: string) => void
  push: (item: StackItem) => void
  root: HTMLElement
} | null>(null)

export const SliderStackProvider = ({ children }: PropsWithChildren) => {
  const [items, setItems] = useState<StackItem[]>([])
  const itemsRef = useRef(items)
  itemsRef.current = items
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const [root] = useState(
    () => document.getElementById('root') ?? document.body,
  )

  const push = useCallback((item: StackItem) => {
    const timer = timers.current.get(item.id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(item.id)
    }
    setItems(items => [...items.filter(i => i.id !== item.id), item])
  }, [])

  const pop = useCallback((id: string) => {
    if (!itemsRef.current.some(i => i.id === id && !i.isClosing)) return
    setItems(items =>
      items.map(i => (i.id === id ? { ...i, isClosing: true } : i)),
    )
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.set(
      id,
      setTimeout(() => {
        setItems(items => items.filter(i => i.id !== id))
        timers.current.delete(id)
      }, CLOSE_MS),
    )
  }, [])

  const dismiss = useCallback((id?: string) => {
    const open = itemsRef.current.filter(i => !i.isClosing)
    ;(id ? open.find(i => i.id === id) : open.at(-1))?.onClose?.()
  }, [])

  const hasOpenLayer = items.some(i => !i.isClosing)

  useEffect(() => {
    if (!hasOpenLayer) return
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && dismiss()
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [dismiss, hasOpenLayer])

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout)
      timers.current.clear()
    },
    [],
  )
  const value = useMemo(
    () => ({ items, pop, push, root }),
    [items, pop, push, root],
  )
  return (
    <Ctx.Provider value={value}>
      {children}
      {createPortal(
        <div
          className={clsx('slider-stack__backdrop', {
            'slider-stack__backdrop--open': hasOpenLayer,
          })}
          onClick={() => dismiss()}
          style={{ zIndex: BASE_Z }}
        />,
        root,
      )}
    </Ctx.Provider>
  )
}

export type SliderLayerProps = PropsWithChildren<{
  className?: string
  isOpen: boolean
  onClose?: () => void
  overflowHidden?: boolean
  position?: 'bottom' | 'left' | 'right'
  width?: string
}>

export const SliderLayer = ({
  children,
  className,
  isOpen,
  onClose,
  overflowHidden = false,
  position = 'right',
  width,
}: SliderLayerProps) => {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('SliderLayer requires SliderStackProvider')

  const { items, pop, push, root } = ctx
  const id = useId()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const shouldRenderContents = isOpen
  const layerWidth = position === 'bottom' ? '100vw' : (width ?? DEFAULT_WIDTH)

  useLayoutEffect(() => {
    if (!isOpen) return
    push({ id, onClose: () => onCloseRef.current?.() })
    return () => pop(id)
  }, [id, isOpen, pop, push])

  const index = items.findIndex(i => i.id === id)
  if (index < 0) {
    return null
  }

  const item = items[index]
  const open = items.filter(i => !i.isClosing)
  const openIndex = open.findIndex(i => i.id === id)
  const layersAbove = openIndex < 0 ? 0 : open.length - 1 - openIndex

  return createPortal(
    <div
      className={clsx('slider-stack__layer', {
        'slider-stack__layer--bottom': position === 'bottom',
        'slider-stack__layer--closing': item.isClosing,
        'slider-stack__layer--left': position === 'left',
        'slider-stack__layer--right': position === 'right',
      })}
      inert={openIndex < 0 || layersAbove > 0}
      style={{ width: layerWidth, zIndex: BASE_Z + 1 + index }}
    >
      <div
        aria-modal={openIndex >= 0 && layersAbove === 0}
        className={clsx('slider-stack__panel', className, {
          'slider-stack__panel--overflow-hidden': overflowHidden,
        })}
        role="dialog"
      >
        {shouldRenderContents && children}
      </div>
      <div
        className="slider-stack__scrim"
        style={{
          opacity:
            SCRIM[Math.min(item.isClosing ? 0 : layersAbove, SCRIM.length - 1)],
        }}
      />
    </div>,
    root,
  )
}
